/**
 * Component Slider - Universal slider for various slider components
 * This provides smooth scrolling functionality for collection circles and other sliders
 */
class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.slider');
    this.sliderItems = this.querySelectorAll('.slider__slide');
    this.pageWidth = window.innerWidth;
    this.slideScrollPosition = 0;
    
    // Performance optimization
    this.isScrolling = false;
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.initialScrollLeft = 0;
    this.initialScrollTop = 0;
    this.hasMoved = false;

    // Track positions for momentum
    this.positions = [];
    this.rafId = null;
    this.autoScrolling = false;
    this.lastTime = 0;
    
    // Number of blocks to scroll at once - standardized to 5 for all sliders
    this.blocksPerScroll = 5;

    if (!this.slider) return;

    // Apply hardware acceleration
    this.slider.style.transform = 'translateZ(0)';
    this.slider.style.willChange = 'scroll-position';

    this.init();
  }

  init() {
    // Set up event listeners
    this.addEventListeners();

    // Initialize auto-scroll if no buttons
    if (this.querySelector('.slider-button') === null) {
      this.setupAutoScroll();
    }

    // Add styling enhancement
    this.slider.style.cursor = 'grab';
  }

  addEventListeners() {
    // Add touch handlers (with passive option for better performance)
    this.slider.addEventListener('touchstart', this.startDrag.bind(this), { passive: true });
    this.slider.addEventListener('touchmove', this.drag.bind(this), { passive: false });
    this.slider.addEventListener('touchend', this.endDrag.bind(this));

    // Add mouse handlers
    this.slider.addEventListener('mousedown', this.startDrag.bind(this));
    window.addEventListener('mousemove', this.drag.bind(this));
    window.addEventListener('mouseup', this.endDrag.bind(this));

    // Prevent default context menu
    this.slider.addEventListener('contextmenu', e => e.preventDefault());

    // Handle clicks on slider items for links
    this.sliderItems.forEach(item => {
      item.addEventListener('click', event => {
        if (this.hasMoved) {
          event.preventDefault();
          event.stopPropagation();
        } else if (item.hasAttribute('data-url')) {
          window.location.href = item.getAttribute('data-url');
        }
      });
    });

    // Handle enter/leave for auto-scroll
    this.slider.addEventListener('mouseenter', () => {
      this.pauseAutoScroll();
    });

    this.slider.addEventListener('mouseleave', () => {
      this.startAutoScroll();
    });

    // Setup button handlers if buttons exist
    const prevButton = this.querySelector('.slider-button--prev');
    const nextButton = this.querySelector('.slider-button--next');

    if (prevButton) {
      prevButton.addEventListener('click', this.onButtonClick.bind(this, 'prev'));
    }

    if (nextButton) {
      nextButton.addEventListener('click', this.onButtonClick.bind(this, 'next'));
    }
  }

  onButtonClick(direction, e) {
    e.preventDefault();
    this.navigateSlider(direction);
  }

  navigateSlider(direction) {
    // Stop auto-scrolling
    this.pauseAutoScroll();
    
    // Calculate scroll amount for exactly 5 blocks
    const slideWidth = this.sliderItems[0].offsetWidth;
    const gapWidth = parseInt(window.getComputedStyle(this.slider).columnGap) || 20;
    
    // Scroll exactly 5 blocks at once across all sliders
    const itemsToScroll = Math.min(this.blocksPerScroll, this.sliderItems.length);
    const scrollAmount = (slideWidth + gapWidth) * itemsToScroll;
    
    // Determine scroll direction
    const scrollOffset = direction === 'next' ? scrollAmount : -scrollAmount;
    
    // Apply smooth scroll
    this.slider.style.scrollBehavior = 'smooth';
    this.slider.scrollBy({
      left: scrollOffset,
      behavior: 'smooth'
    });
    
    // Resume auto-scroll after a delay
    setTimeout(() => {
      if (!this.slider.matches(':hover')) {
        this.startAutoScroll();
      }
    }, 2000);
  }

  startDrag(e) {
    if (this.isScrolling) return;
    const isTouch = e.type.includes('touch');
    
    // Get pageX/pageY coordinates
    this.startX = isTouch ? e.touches[0].pageX : e.pageX;
    this.startY = isTouch ? e.touches[0].pageY : e.pageY;
    this.currentX = this.startX;
    this.currentY = this.startY;

    // Store initial scroll position
    this.initialScrollLeft = this.slider.scrollLeft;
    this.initialScrollTop = window.scrollY;
    
    // Track for momentum
    this.positions = [{time: Date.now(), position: this.startX}];
    
    // Start drag mode
    this.isDragging = true;
    this.hasMoved = false;
    
    // Set visual indicators
    this.slider.classList.add('dragging');
    this.slider.style.cursor = 'grabbing';
    
    // Pause auto-scroll during drag
    this.pauseAutoScroll();
    
    // For mouse events, prevent default to avoid text selection
    if (!isTouch) {
      e.preventDefault();
    }

    // Cancel any momentum animation
    cancelAnimationFrame(this.rafId);
  }

  drag(e) {
    if (!this.isDragging) return;
    
    const isTouch = e.type.includes('touch');
    const currentX = isTouch ? e.touches[0].pageX : e.pageX;
    const currentY = isTouch ? e.touches[0].pageY : e.pageY;
    
    // Calculate the movement distance
    const deltaX = this.startX - currentX;
    const deltaY = this.startY - currentY;
    
    // Determine if scroll is more horizontal or vertical
    if (!this.hasMoved) {
      // If movement is more vertical than horizontal, allow page scroll
      if (Math.abs(deltaY) > Math.abs(deltaX) + 5) {
        this.isDragging = false;
        this.slider.classList.remove('dragging');
        return;
      }
      
      // Only count as a move if moved more than 5px horizontally
      if (Math.abs(deltaX) > 5) {
        this.hasMoved = true;
      }
    }
    
    // If definitely horizontal movement, prevent page scroll
    if (this.hasMoved && isTouch && e.cancelable) {
      e.preventDefault();
    }
    
    // Update position for momentum calculation
    this.currentX = currentX;
    this.positions.push({time: Date.now(), position: currentX});
    if (this.positions.length > 5) this.positions.shift();
    
    // Apply scroll to slider with hardware acceleration
    requestAnimationFrame(() => {
      if (this.isDragging) {
        this.slider.scrollLeft = this.initialScrollLeft + deltaX;
      }
    });
  }

  endDrag() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.slider.classList.remove('dragging');
    this.slider.style.cursor = 'grab';
    
    if (this.hasMoved && this.positions.length >= 2) {
      // Calculate velocity for momentum
      const lastPosition = this.positions[this.positions.length - 1];
      const firstPosition = this.positions[0];
      const deltaTime = lastPosition.time - firstPosition.time;
      const deltaPosition = lastPosition.position - firstPosition.position;
      
      if (deltaTime > 0) {
        // Apply momentum with velocity
        const velocity = deltaPosition / deltaTime;
        this.applyMomentum(velocity);
      }
    }
    
    // Resume auto-scroll after a delay
    setTimeout(() => {
      if (!this.slider.matches(':hover')) {
        this.startAutoScroll();
      }
    }, 2000);
  }

  applyMomentum(velocity) {
    if (Math.abs(velocity) < 0.1) return;
    
    // Calculate the target scroll based on 5 slides at once
    const slideWidth = this.sliderItems[0].offsetWidth;
    const gapWidth = parseInt(window.getComputedStyle(this.slider).columnGap) || 20;
    const singleItemWidth = slideWidth + gapWidth;
    
    // Determine how far to scroll based on velocity
    const direction = velocity < 0 ? 1 : -1;
    const scrollAmount = direction * singleItemWidth * this.blocksPerScroll;
    
    // Calculate target position (current position + momentum-based scroll)
    const targetScrollLeft = this.slider.scrollLeft + scrollAmount;
    
    // Use smooth animation to scroll to target
    const start = Date.now();
    const initialScrollLeft = this.slider.scrollLeft;
    const duration = 600; // ms
    
    const momentumStep = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(1, elapsed / duration);
      const easedProgress = this.cubicBezier(0.33, 0.1, 0.25, 1, progress);
      
      if (progress < 1) {
        this.slider.scrollLeft = initialScrollLeft + (targetScrollLeft - initialScrollLeft) * easedProgress;
        this.rafId = requestAnimationFrame(momentumStep);
      } else {
        this.slider.scrollLeft = targetScrollLeft;
        cancelAnimationFrame(this.rafId);
      }
    };
    
    this.rafId = requestAnimationFrame(momentumStep);
  }

  // Easing function
  cubicBezier(p1x, p1y, p2x, p2y, t) {
    const cx = 3 * p1x;
    const bx = 3 * (p2x - p1x) - cx;
    const ax = 1 - cx - bx;
    
    const cy = 3 * p1y;
    const by = 3 * (p2y - p1y) - cy;
    const ay = 1 - cy - by;
    
    function sampleCurveX(t) {
      return ((ax * t + bx) * t + cx) * t;
    }
    
    function sampleCurveY(t) {
      return ((ay * t + by) * t + cy) * t;
    }
    
    function solveCurveX(x) {
      let t0 = 0;
      let t1 = 1;
      let t2 = x;
      
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      
      for (let i = 0; i < 8; i++) {
        const x2 = sampleCurveX(t2);
        if (Math.abs(x2 - x) < 0.001) return t2;
        
        const d2 = (3 * ax * t2 + 2 * bx) * t2 + cx;
        if (Math.abs(d2) < 1e-6) break;
        
        t2 = t2 - (x2 - x) / d2;
      }
      
      // Fall back to bisection method for convergence
      while (t0 < t1) {
        const x2 = sampleCurveX(t2);
        if (Math.abs(x2 - x) < 0.001) return t2;
        
        if (x > x2) t0 = t2;
        else t1 = t2;
        
        t2 = (t1 - t0) * 0.5 + t0;
      }
      
      return t2; // Give up, close enough
    }
    
    return sampleCurveY(solveCurveX(t));
  }

  pauseAutoScroll() {
    this.autoScrolling = false;
    cancelAnimationFrame(this.rafId);
  }

  startAutoScroll() {
    // Only auto-scroll if no buttons present
    if (this.querySelector('.slider-button') !== null) return;
    if (this.sliderItems.length <= 1 || this.autoScrolling) return;
    
    this.autoScrolling = true;
    const autoScrollSpeed = 0.6; // Faster to make auto-scroll more visible
    
    const step = (timestamp) => {
      if (!this.autoScrolling) return;
      
      // Calculate time elapsed
      const elapsed = this.lastTime ? timestamp - this.lastTime : 16.7;
      this.lastTime = timestamp;
      
      // Calculate pixels to move
      const pixelsToMove = (autoScrollSpeed * elapsed) / 16.7;
      
      // Apply scroll
      this.slider.scrollLeft += pixelsToMove;
      
      // Handle infinite loop
      if (this.slider.scrollLeft >= this.slider.scrollWidth - this.slider.clientWidth - 20) {
        // Jump back to start when reaching the end
        this.slider.scrollLeft = 0;
      }
      
      // Continue animation
      this.rafId = requestAnimationFrame(step);
    };
    
    this.rafId = requestAnimationFrame(step);
  }

  setupAutoScroll() {
    // Wait a moment before starting auto-scroll
    setTimeout(() => {
      this.startAutoScroll();
    }, 2000);
  }
}

customElements.define('slider-component', SliderComponent); 